/**
 * CallersEndpointsPanel — Issue #556 (was ArtifactExplorerPanel) + #995/#996.
 *
 * Sidebar panel for the E2E Flows catalog view. Derives API callers
 * and endpoints from the loaded flows data; clicking an item filters
 * the catalog to show only relevant flows.
 *
 * Renamed from "ArtifactExplorerPanel" per #995 — "Callers &
 * Endpoints" matches developer vocabulary; "artifact" is analyzer-
 * pipeline jargon. Defaults to open on the page per #996.
 */

import { useMemo, useState } from 'react';
import type { E2EFlowPath } from '@veodiagram/shared';

interface CallersEndpointsPanelProps {
  flows: E2EFlowPath[];
  /** Currently active caller filter (sourceFile:sourceContext) */
  activeCaller: string | null;
  /** Currently active endpoint filter (METHOD /path) */
  activeEndpoint: string | null;
  onSelectCaller: (key: string | null) => void;
  onSelectEndpoint: (key: string | null) => void;
  /** #996: explicit collapse control from within the panel header. */
  onCollapse?: () => void;
}

interface CallerInfo {
  key: string;
  file: string;
  context: string;
  flowCount: number;
  endpointCount: number;
}

interface EndpointInfo {
  key: string;
  method: string;
  endpoint: string;
  flowCount: number;
  callerCount: number;
}

export function CallersEndpointsPanel({
  flows,
  activeCaller,
  activeEndpoint,
  onSelectCaller,
  onSelectEndpoint,
  onCollapse,
}: CallersEndpointsPanelProps) {
  const [expandCallers, setExpandCallers] = useState(true);
  const [expandEndpoints, setExpandEndpoints] = useState(true);
  const [callerSearch, setCallerSearch] = useState('');
  const [endpointSearch, setEndpointSearch] = useState('');

  const { callers, endpoints } = useMemo(() => {
    const callerMap = new Map<string, { file: string; context: string; flows: Set<string>; endpoints: Set<string> }>();
    const endpointMap = new Map<string, { method: string; endpoint: string; flows: Set<string>; callers: Set<string> }>();

    for (const flow of flows) {
      for (const call of flow.apiCalls ?? []) {
        const callerKey = `${call.sourceFile}|${call.sourceContext ?? ''}`;
        const endpointKey = `${call.method} ${call.endpoint}`;

        // Track callers
        const existing = callerMap.get(callerKey);
        if (existing) {
          existing.flows.add(flow.id);
          existing.endpoints.add(endpointKey);
        } else {
          const parts = call.sourceFile.split('/');
          callerMap.set(callerKey, {
            file: parts.slice(-2).join('/'),
            context: call.sourceContext ?? '',
            flows: new Set([flow.id]),
            endpoints: new Set([endpointKey]),
          });
        }

        // Track endpoints
        const ep = endpointMap.get(endpointKey);
        if (ep) {
          ep.flows.add(flow.id);
          ep.callers.add(callerKey);
        } else {
          endpointMap.set(endpointKey, {
            method: call.method,
            endpoint: call.endpoint,
            flows: new Set([flow.id]),
            callers: new Set([callerKey]),
          });
        }
      }
    }

    const callers: CallerInfo[] = Array.from(callerMap.entries())
      .map(([key, v]) => ({
        key,
        file: v.file,
        context: v.context,
        flowCount: v.flows.size,
        endpointCount: v.endpoints.size,
      }))
      .sort((a, b) => b.flowCount - a.flowCount);

    const endpoints: EndpointInfo[] = Array.from(endpointMap.entries())
      .map(([key, v]) => ({
        key,
        method: v.method,
        endpoint: v.endpoint,
        flowCount: v.flows.size,
        callerCount: v.callers.size,
      }))
      .sort((a, b) => b.flowCount - a.flowCount);

    return { callers, endpoints };
  }, [flows]);

  const filteredCallers = useMemo(() => {
    if (!callerSearch) return callers;
    const q = callerSearch.toLowerCase();
    return callers.filter(c => c.file.toLowerCase().includes(q) || c.context.toLowerCase().includes(q));
  }, [callers, callerSearch]);

  const filteredEndpoints = useMemo(() => {
    if (!endpointSearch) return endpoints;
    const q = endpointSearch.toLowerCase();
    return endpoints.filter(e => e.endpoint.toLowerCase().includes(q) || e.method.toLowerCase().includes(q));
  }, [endpoints, endpointSearch]);

  const methodColor = (method: string) => {
    switch (method.toUpperCase()) {
      case 'GET': return 'text-green-700 bg-green-50';
      case 'POST': return 'text-blue-700 bg-blue-50';
      case 'PUT': case 'PATCH': return 'text-amber-700 bg-amber-50';
      case 'DELETE': return 'text-red-700 bg-red-50';
      default: return 'text-slate-700 bg-slate-100';
    }
  };

  const hasActive = activeCaller !== null || activeEndpoint !== null;

  return (
    <div
      data-testid="callers-endpoints-panel"
      className="w-64 flex-shrink-0 bg-white border-r border-slate-200 dark:bg-slate-800 dark:border-slate-700 flex flex-col h-full overflow-hidden"
    >
      {/* Header — #995 renamed; #996 explicit collapse control. */}
      <div className="px-3 py-2.5 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wide truncate">
          Callers &amp; Endpoints
        </span>
        <div className="flex items-center gap-2 shrink-0">
          {hasActive && (
            <button
              onClick={() => { onSelectCaller(null); onSelectEndpoint(null); }}
              className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
            >
              Clear filter
            </button>
          )}
          {onCollapse && (
            <button
              data-testid="callers-endpoints-collapse"
              onClick={onCollapse}
              className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 p-0.5"
              title="Hide panel"
              aria-label="Hide callers and endpoints panel"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Callers Section */}
        <div>
          <button
            onClick={() => setExpandCallers(v => !v)}
            className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-700"
          >
            <div className="flex items-center gap-1.5">
              <span className={`text-xs transition-transform ${expandCallers ? 'rotate-90' : ''}`}>▶</span>
              <span className="text-xs font-medium text-slate-700 dark:text-slate-300">API Callers</span>
              <span className="text-xs text-slate-400 ml-1">({callers.length})</span>
            </div>
          </button>

          {expandCallers && (
            <div className="pb-2">
              {/* #996: helper text describing the filter behaviour. */}
              <p className="px-3 pb-1 text-[10px] text-slate-400 dark:text-slate-500 leading-snug">
                Frontend code locations that originate api-calls. Click to filter the catalog to flows from this caller.
              </p>
              <div className="px-3 pb-2">
                <input
                  type="text"
                  placeholder="Search callers…"
                  value={callerSearch}
                  onChange={e => setCallerSearch(e.target.value)}
                  className="w-full px-2 py-1 text-xs border border-slate-200 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200"
                />
              </div>
              <div className="space-y-0.5 px-2">
                {filteredCallers.length === 0 && (
                  <p className="text-xs text-slate-400 px-1 py-2 italic">No callers found</p>
                )}
                {filteredCallers.map(c => (
                  <button
                    key={c.key}
                    onClick={() => onSelectCaller(activeCaller === c.key ? null : c.key)}
                    className={`w-full text-left px-2 py-1.5 rounded text-xs transition-colors ${
                      activeCaller === c.key
                        ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200'
                        : 'hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300'
                    }`}
                  >
                    <div className="font-mono truncate" title={c.file}>{c.file}</div>
                    {c.context && (
                      <div className="text-slate-400 dark:text-slate-500 truncate mt-0.5" title={c.context}>
                        {c.context}
                      </div>
                    )}
                    <div className="flex gap-2 mt-0.5 text-slate-400 dark:text-slate-500">
                      <span>{c.flowCount} flow{c.flowCount !== 1 ? 's' : ''}</span>
                      <span>{c.endpointCount} endpoint{c.endpointCount !== 1 ? 's' : ''}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="border-t border-slate-100 dark:border-slate-700" />

        {/* Endpoints Section */}
        <div>
          <button
            onClick={() => setExpandEndpoints(v => !v)}
            className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-700"
          >
            <div className="flex items-center gap-1.5">
              <span className={`text-xs transition-transform ${expandEndpoints ? 'rotate-90' : ''}`}>▶</span>
              <span className="text-xs font-medium text-slate-700 dark:text-slate-300">Endpoints</span>
              <span className="text-xs text-slate-400 ml-1">({endpoints.length})</span>
            </div>
          </button>

          {expandEndpoints && (
            <div className="pb-2">
              {/* #996: helper text describing the filter behaviour. */}
              <p className="px-3 pb-1 text-[10px] text-slate-400 dark:text-slate-500 leading-snug">
                Backend routes the flows reach. Click to filter the catalog to flows that hit this endpoint.
              </p>
              <div className="px-3 pb-2">
                <input
                  type="text"
                  placeholder="Search endpoints…"
                  value={endpointSearch}
                  onChange={e => setEndpointSearch(e.target.value)}
                  className="w-full px-2 py-1 text-xs border border-slate-200 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200"
                />
              </div>
              <div className="space-y-0.5 px-2">
                {filteredEndpoints.length === 0 && (
                  <p className="text-xs text-slate-400 px-1 py-2 italic">No endpoints found</p>
                )}
                {filteredEndpoints.map(e => (
                  <button
                    key={e.key}
                    onClick={() => onSelectEndpoint(activeEndpoint === e.key ? null : e.key)}
                    className={`w-full text-left px-2 py-1.5 rounded text-xs transition-colors ${
                      activeEndpoint === e.key
                        ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200'
                        : 'hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300'
                    }`}
                  >
                    <div className="flex items-center gap-1.5">
                      <span className={`px-1.5 py-0.5 rounded font-mono font-semibold uppercase ${methodColor(e.method)}`}>
                        {e.method}
                      </span>
                      <span className="font-mono truncate text-slate-700 dark:text-slate-300" title={e.endpoint}>
                        {e.endpoint}
                      </span>
                    </div>
                    <div className="flex gap-2 mt-0.5 text-slate-400 dark:text-slate-500">
                      <span>{e.flowCount} flow{e.flowCount !== 1 ? 's' : ''}</span>
                      <span>{e.callerCount} caller{e.callerCount !== 1 ? 's' : ''}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * #996: Collapsed-state strip — a thin vertical bar with a
 * chevron-right + label so the user can re-open the panel after
 * collapsing it.
 */
export function CallersEndpointsCollapsedStrip({ onExpand }: { onExpand: () => void }) {
  return (
    <button
      data-testid="callers-endpoints-expand"
      onClick={onExpand}
      title="Show callers and endpoints panel"
      aria-label="Show callers and endpoints panel"
      className="w-10 flex-shrink-0 bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 flex flex-col items-center justify-start py-3 gap-2 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
    >
      <svg className="w-4 h-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
      </svg>
      <span
        className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider"
        style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
      >
        Callers &amp; Endpoints
      </span>
    </button>
  );
}
